import {
  DAY, ROUTES, WEEK_HOURS_CAP, categoryKeyFor, enrolled, isActive, scheduleOf, scheduleProgress, weekBounds,
} from "../shared/classData";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CAT_COLORS = {
  fire: "var(--color-cat-firefighter)", ems: "var(--color-cat-ems)", aha: "var(--color-cat-aha)", res: "var(--color-cat-rescue)",
};
const CRED_WARN_DAYS = 60;
const HOURS_WEEKS = 8;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const clock = (d) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
const colorFor = (c) => CAT_COLORS[categoryKeyFor(c.category, c.title)];
const shortDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function greeting(now) {
  const h = now.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

/** Every session of the instructor's classes as {class, start, end, location}, soonest first. */
function allSessions(classes) {
  return classes
    .filter((c) => c.publishStatus !== "DRAFT")
    .flatMap((c) => scheduleOf(c).map((s) => ({ c, start: new Date(s.start), end: new Date(s.end), location: s.location || "" })))
    .filter((s) => !Number.isNaN(+s.start))
    .sort((a, b) => a.start - b.start);
}

const hoursOf = (s) => (s.end > s.start ? (s.end - s.start) / 3600000 : 0);

function buildToday(sessions, now) {
  const today = startOfDay(now);
  return sessions.filter((s) => +startOfDay(s.start) === +today).map((s) => ({
    id: `${s.c.id}-${s.start.toISOString()}`,
    courseId: s.c.id,
    title: s.c.title || "Session",
    code: s.c.courseNumber || "",
    color: colorFor(s.c),
    start: clock(s.start),
    end: clock(s.end),
    location: s.location,
    learners: enrolled(s.c),
    state: s.end <= now ? "done" : s.start <= now ? "live" : "next",
  }));
}

function buildWeek(sessions, now) {
  const { start } = weekBounds(now);
  const today = startOfDay(now);
  return DAY_NAMES.map((name, i) => {
    const date = new Date(+start + i * DAY);
    return {
      name, day: date.getDate(), today: +date === +today,
      sessions: sessions.filter((s) => +startOfDay(s.start) === +date).map((s) => ({ id: `${s.c.id}-${s.start.toISOString()}`, time: clock(s.start), code: s.c.courseNumber || s.c.title, color: colorFor(s.c), courseId: s.c.id })),
    };
  });
}

/** Scheduled teaching hours for the last 8 Monday-start weeks (the last bar is this week). */
function buildHours(sessions, now) {
  const { start: thisMonday } = weekBounds(now);
  const weeks = Array.from({ length: HOURS_WEEKS }, (_, i) => {
    const begin = new Date(+thisMonday - (HOURS_WEEKS - 1 - i) * 7 * DAY);
    return { begin, end: new Date(+begin + 7 * DAY), hours: 0 };
  });
  sessions.forEach((s) => {
    const w = weeks.find((x) => s.start >= x.begin && s.start < x.end);
    if (w) w.hours += hoursOf(s);
  });
  return weeks.map((w, i) => ({ label: i === HOURS_WEEKS - 1 ? "Now" : i % 2 === 0 ? shortDate(w.begin) : "", hours: Math.round(w.hours) }));
}

function buildClasses(classes, now, go) {
  return classes.filter((c) => isActive(c, now)).sort((a, b) => new Date(a.date) - new Date(b.date)).map((c) => {
    const prog = scheduleProgress(c, now);
    const upcoming = new Date(c.date) > now;
    return {
      id: c.id, name: c.title || "Class", code: c.courseNumber || "", color: colorFor(c), learners: enrolled(c),
      pct: prog && !upcoming ? prog.pct : 0,
      detail: upcoming ? `Starts ${shortDate(new Date(c.date))}` : prog ? `Session ${Math.min(prog.done + 1, prog.total)} of ${prog.total}` : "No sessions yet",
      onClick: () => go(ROUTES.courseHub(c.id)),
    };
  });
}

function credentialRows(certs, now) {
  return certs.filter((c) => c.expires).map((c) => ({ ...c, days: Math.round((startOfDay(new Date(c.expires)) - startOfDay(now)) / DAY) }))
    .sort((a, b) => a.days - b.days);
}

const TASK_STYLE = { SIGN_OFF: ["w", "clip", "Grade"], ATTENDANCE_OPEN: ["r", "clock", "Submit"] };

function buildAttention(tasks, certs, now, go) {
  const rows = tasks.map((t, i) => {
    const [tone, icon, cta] = TASK_STYLE[t.type] || ["v", "doc", "Open"];
    return { id: `task-${t.type}-${i}`, tone, icon, title: t.title, detail: t.courseName || t.description || "Review class progress", cta, onClick: () => go(t.link || ROUTES.instructorCourses) };
  });
  credentialRows(certs, now).filter((c) => c.days <= CRED_WARN_DAYS).forEach((c) => {
    rows.push({ id: `cred-${c.id}`, tone: c.days < 0 ? "r" : "v", icon: "cert", title: c.days < 0 ? `${c.name} expired` : `${c.name} expires in ${c.days} day${c.days === 1 ? "" : "s"}`, detail: `${c.issuer || "Issuer"} · ${new Date(c.expires).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`, cta: "Renew", onClick: () => go(ROUTES.certifications) });
  });
  return rows;
}

/** Pure view-model builder for InstructorDashboard; takes already-fetched data (see classData.js for the class payload). */
export function buildInstructorModel({ loading = false, classes = [], tasks = [], signoffs = [], stats = {}, certs = [], user, tenantConfig, navigate = () => {}, now = new Date() }) {
  const sessions = allSessions(classes);
  const today = buildToday(sessions, now);
  const live = today.find((s) => s.state === "live") || null;
  const next = today.find((s) => s.state === "next") || null;
  const hours = buildHours(sessions, now);
  const thisWeek = hours[hours.length - 1].hours;
  const name = (user?.fullName || user?.name || user?.username?.split("@")[0] || "there");
  const tenantName = tenantConfig?.name || "Training Center";
  const attention = buildAttention(tasks, certs, now, navigate);
  const signoffClasses = new Set(signoffs.map((s) => s.courseId)).size;
  return {
    loading, tenantName,
    header: {
      eyebrow: `${tenantName} · ${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`,
      greeting: `${greeting(now)}, ${name}`,
    },
    hero: {
      live,
      next,
      sessionsToday: today.length,
      sessionsDone: today.filter((s) => s.state === "done").length,
      signoffs: signoffs.length,
      signoffClasses,
      hoursThisWeek: thisWeek,
      hoursLeft: Math.max(0, WEEK_HOURS_CAP - thisWeek),
    },
    today,
    week: { label: `${shortDate(weekBounds(now).start)} – ${shortDate(new Date(+weekBounds(now).start + 6 * DAY))}`, days: buildWeek(sessions, now) },
    classes: buildClasses(classes, now, navigate),
    hours,
    attention,
    signoffs: signoffs.map((s, i) => ({
      id: `${s.courseId}-${s.checklistId ?? i}-${s.studentId ?? i}`, student: s.studentName || "Learner", title: s.checklistTitle || "Skill sheet", course: s.courseName || "",
      onClick: () => navigate(s.courseId && s.checklistId && s.studentId ? `/instructor/courses/${s.courseId}/checklists/${s.checklistId}/signoff/${s.studentId}` : ROUTES.instructorCourses),
    })),
    certs: credentialRows(certs, now),
    attendance: stats?.averageAttendance != null ? Number(stats.averageAttendance) : null,
    navigate,
  };
}
